import React, { useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Flex, Link, Text, Button, QueryLoader, toast } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { useComplianceCaseContext } from "../../../entrypoints/ComplianceCaseContext";
import {
  AccountTypeEnum,
  ComplianceObjectType,
  DocumentUploadInviteStatus,
} from "../../../graphql/supergraphEnums";
import {
  useGetDocumentDetailsQuery,
  useCancelUploadInviteMutation,
  useResendUploadInviteMutation,
} from "../../../graphql/supergraphHooks";
import {
  type DocumentType,
  type DocumentUploadInviteDetails,
} from "../../../graphql/supergraphTypes";
import useDocumentProcessor from "../../../hooks/documents/useDocumentProcessor";
import {
  isIndividualAccountType,
  isJoinAccountType,
  resolvedAccountType,
} from "../../../utils/account_types";
import { formatDate } from "../../../utils/date_formatter";
import { getErrorMessage } from "../../../utils/error_parser";
import { clientContext } from "../../../utils/graphql_utils";
import { DefaultModal } from "../../common/DefaultModal";
import { sharedHeaderStyle } from "../../common/style_vanilla.css";
import { provideLegalName } from "../../ProfileTree/utils";
import { DocumentCollector } from "../DocumentCollector";
import { DocumentData, DocumentInvitedUserInfo } from "../DocumentCollector/types";
import { InviteToUploadModal } from "../InviteToUploadModal";
import {
  buttonText,
  inviteCompleteWrapper,
  headerWrapper,
  inviteCompleteMessageText,
  infoText,
} from "./style_vanilla.css";
import { mapDocumentsToFormProps } from "./utils";

export const DocumentCollectorInvestor = ({
  onStepCompletion,
}: {
  onStepCompletion: () => void;
}) => {
  const history = useHistory();
  const { profileId } = useParams<{ profileId: string }>();
  const { rootProfileId, basePath, caseId, isReadOnly, skipDetailsForRootProfile } =
    useComplianceCaseContext();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitedUser, setInvitedUser] = useState<DocumentInvitedUserInfo | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [allDocumentsReady, setAllDocumentsReady] = useState(false);
  const [showBackWarningModal, setShowBackWarningModal] = useState(false);

  const parsedProfileId = profileId ? parseInt(profileId) : rootProfileId;

  const { processDocuments, loading } = useDocumentProcessor(caseId, parsedProfileId);

  const [cancelUploadInvite] = useCancelUploadInviteMutation({
    context: clientContext(),
  });
  const [resendUploadInvite] = useResendUploadInviteMutation({
    context: clientContext(),
  });
  const documentDetailsResult = useGetDocumentDetailsQuery({
    context: clientContext(),
    fetchPolicy: "no-cache",
    variables: {
      profileId: parsedProfileId,
      caseId: caseId,
    },
  });

  const isRoot = parsedProfileId === rootProfileId;

  const complianceCase = useMemo(() => {
    const result = documentDetailsResult.data?.complianceProfile?.complianceCases?.find(
      (complianceCase) => complianceCase.caseId === caseId,
    );
    return result;
  }, [documentDetailsResult.data?.complianceProfile?.complianceCases, caseId]);

  const profileName = useMemo(
    () => provideLegalName(documentDetailsResult.data?.complianceProfile),
    [documentDetailsResult.data?.complianceProfile],
  );

  const isInvestingProfile = useMemo(
    () => Boolean(complianceCase?.isInvestingProfile),
    [complianceCase?.isInvestingProfile],
  );

  const investingProfile = useMemo(
    () => complianceCase?.complianceCase?.investingProfile,
    [complianceCase?.complianceCase?.investingProfile],
  );

  const complianceProfile = useMemo(
    () => documentDetailsResult.data?.complianceProfile,
    [documentDetailsResult.data?.complianceProfile],
  );

  const investorAccountType = useMemo(
    () => resolvedAccountType(investingProfile),
    [investingProfile],
  );

  // for ira account, we consider it as individual account
  const isIndividualInvestor = useMemo(
    () =>
      Boolean(
        isIndividualAccountType(investorAccountType) ||
          investingProfile?.complianceObjectType === ComplianceObjectType.Individual,
      ),
    [investingProfile?.complianceObjectType, investorAccountType],
  );

  const showLinkToIraSelector = useMemo(
    () =>
      investorAccountType === AccountTypeEnum.Ira &&
      isIndividualInvestor &&
      skipDetailsForRootProfile,
    [isIndividualInvestor, skipDetailsForRootProfile, investorAccountType],
  );
  const documentsOnlyScenario = useMemo(
    () => isIndividualInvestor && skipDetailsForRootProfile,
    [isIndividualInvestor, skipDetailsForRootProfile],
  );

  const documentDetails = useMemo(() => {
    // For joint accounts, we use the root profile's rules engine
    // For other accounts, we use the profile's rules engine
    const documentTypes = isJoinAccountType(investorAccountType)
      ? investingProfile?.rulesEngine?.documentTypes
      : complianceProfile?.rulesEngine?.documentTypes;
    const documents = (complianceProfile?.documents || []) as DocumentType[];
    return mapDocumentsToFormProps(documentTypes || [], documents);
  }, [
    investorAccountType,
    investingProfile?.rulesEngine?.documentTypes,
    complianceProfile?.rulesEngine?.documentTypes,
    complianceProfile?.documents,
  ]);

  const inviteDetails: DocumentUploadInviteDetails | null | undefined =
    documentDetailsResult.data?.complianceProfile?.inviteToUploadDocumentsDetails;

  const statusBannerInfo = useMemo(() => {
    switch (inviteDetails?.status) {
      case DocumentUploadInviteStatus.UploadPending:
        return {
          message: `${inviteDetails.emailAddress} was invited to complete on ${formatDate(inviteDetails?.emailInviteDateSent as string)}`,
          inviteId: inviteDetails?.id,
        };
      case DocumentUploadInviteStatus.UploadSkipped:
        return {
          message: `${inviteDetails.emailAddress} indicated they could not provide any requested documents on ${formatDate(inviteDetails?.documentUploadDate as string)}`,
        };
      case DocumentUploadInviteStatus.UploadCompleted:
        return {
          message: `${inviteDetails.emailAddress} uploaded documents on ${formatDate(inviteDetails?.documentUploadDate as string)}`,
        };
      default:
        return null;
    }
  }, [inviteDetails]);

  const disableInviteButton = useMemo(
    () =>
      inviteDetails?.status === DocumentUploadInviteStatus.UploadPending || allDocumentsReady,
    [inviteDetails?.status, allDocumentsReady],
  );

  /**
   * Handle unsaved changes updates from DocumentCollector
   */
  const handleUnsavedChangesChange = useCallback((hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges);
  }, []);

  /**
   * Handle when all documents are ready (have files attached)
   */
  const handleAllDocumentsReadyChange = useCallback((allReady: boolean) => {
    setAllDocumentsReady(allReady);
  }, []);

  const navigateBack = useCallback(() => {
    if (isIndividualInvestor) {
      history.goBack();
    } else {
      history.push(isRoot ? basePath : `${basePath}/overview/${parsedProfileId}`);
    }
  }, [basePath, isRoot, parsedProfileId, isIndividualInvestor, history]);

  const closePageOnCompletion = useCallback(() => {
    if (isIndividualInvestor) {
      documentsOnlyScenario ? onStepCompletion() : history.goBack();
    } else {
      history.push(isRoot ? basePath : `${basePath}/overview/${parsedProfileId}`);
    }
  }, [
    basePath,
    isRoot,
    parsedProfileId,
    isIndividualInvestor,
    history,
    documentsOnlyScenario,
    onStepCompletion,
  ]);

  /**
   * Handle navigation back with unsaved changes check
   */
  const handleNavigateBack = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowBackWarningModal(true);
    } else {
      navigateBack();
    }
  }, [hasUnsavedChanges, navigateBack]);

  /**
   * Process documents and handle success/error feedback
   * @param formValues - The document form values to process
   */
  const handleDocumentProcessing = useCallback(
    (newFiles: DocumentData[], deleteDocIds: number[]) => {
      processDocuments(newFiles, deleteDocIds, true)
        .then(() => {
          const documentsUploaded = newFiles.some((document) => document.file != null);
          if (documentsUploaded || deleteDocIds.length > 0) {
            toast.success("Documents updated successfully");
          }
          setHasUnsavedChanges(false); // Clear unsaved changes after successful submission
          closePageOnCompletion();
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
        });
    },
    [processDocuments, closePageOnCompletion],
  );

  const handleCancelUploadInvite = useCallback(() => {
    if (
      inviteDetails?.status !== DocumentUploadInviteStatus.UploadCompleted &&
      inviteDetails?.id
    ) {
      void cancelUploadInvite({
        variables: {
          inviteId: inviteDetails.id,
        },
      });
    }
  }, [cancelUploadInvite, inviteDetails]);

  const showInviteButton = useMemo(
    () => !(isInvestingProfile || isJoinAccountType(investorAccountType)),
    [isInvestingProfile, investorAccountType],
  );

  return (
    <QueryLoader result={documentDetailsResult}>
      <Flex flexDirection="column">
        {!isReadOnly && (
          <>
            {showLinkToIraSelector ? (
              <Link
                to={`${basePath}/ira-categorization`}
                data-testid="beneficial-owner-selection-back-link"
                variant="muted"
                icon="arrow-left"
                iconPosition="left"
                align="left"
              >
                Select account type
              </Link>
            ) : (
              <>
                {!documentsOnlyScenario && (
                  <Link
                    to={isRoot ? basePath : `${basePath}/overview/${parsedProfileId}`}
                    variant="muted"
                    icon="arrow-left"
                    iconPosition="left"
                    align="left"
                  >
                    {profileName}
                  </Link>
                )}
              </>
            )}
          </>
        )}
        <Flex className={headerWrapper}>
          <Text variant="h3" className={sharedHeaderStyle}>
            {isIndividualInvestor ? "Identification" : `Documents for ${profileName}`}
          </Text>
          {showInviteButton && (
            <Button
              variant="neutral"
              icon="file-upload"
              disabled={disableInviteButton}
              onClick={() => setShowInviteModal(true)}
            >
              Invite to complete
            </Button>
          )}
        </Flex>
        <Text
          variant="regular-md"
          className={infoText}
        >{`Due to regulatory requirements, the following documents are required.`}</Text>
        {statusBannerInfo && (
          <Flex className={inviteCompleteWrapper}>
            <Text variant="regular-md" className={inviteCompleteMessageText}>
              {statusBannerInfo?.message}
            </Text>
            {statusBannerInfo?.inviteId && (
              <Flex gap={Sizes.SM}>
                <Text
                  variant="strong-md"
                  className={buttonText}
                  onClick={() => {
                    void resendUploadInvite({
                      variables: {
                        inviteId: statusBannerInfo.inviteId,
                      },
                    }).then(() => {
                      toast.success("Invite resent successfully");
                      void documentDetailsResult.refetch();
                    });
                  }}
                >
                  Resend
                </Text>
                <Text
                  variant="strong-md"
                  className={buttonText}
                  onClick={() => {
                    setInvitedUser({
                      id: statusBannerInfo.inviteId,
                      emailAddress: inviteDetails?.emailAddress || "",
                    });
                  }}
                >
                  Cancel
                </Text>
              </Flex>
            )}
          </Flex>
        )}
        <DocumentCollector
          isSubmitting={loading}
          documentDetails={documentDetails}
          onNavigateBack={handleNavigateBack}
          onComplete={handleDocumentProcessing}
          onUnsavedChangesChange={handleUnsavedChangesChange}
          onAllDocumentsReadyChange={handleAllDocumentsReadyChange}
          preUploadCallback={handleCancelUploadInvite}
        />
      </Flex>
      <InviteToUploadModal
        show={showInviteModal}
        onClose={(inviteDetails) => {
          setShowInviteModal(false);
          if (inviteDetails) {
            void documentDetailsResult.refetch();
          }
        }}
        caseId={caseId}
        profileId={parsedProfileId}
      />
      <DefaultModal
        show={!!invitedUser}
        title="Warning"
        body={`Are you sure you want to cancel the invitation to complete sent to ${invitedUser?.emailAddress}? A new invitation can be sent once this invitation is canceled.`}
        negBtnText="Close"
        posBtnText="Cancel invitation"
        posBtnVariant="danger"
        onClose={() => setInvitedUser(null)}
        onConfirm={() => {
          if (!invitedUser) {
            return;
          }
          void cancelUploadInvite({
            variables: {
              inviteId: invitedUser.id,
            },
          }).then(() => {
            void documentDetailsResult.refetch();
            setInvitedUser(null);
          });
        }}
      />
      <DefaultModal
        show={showBackWarningModal}
        title="Warning"
        body="You have unsaved changes. Are you sure you want to go back without submitting?"
        negBtnText="Cancel"
        posBtnText="Back"
        posBtnVariant="danger"
        onClose={() => setShowBackWarningModal(false)}
        onConfirm={() => {
          setShowBackWarningModal(false);
          setHasUnsavedChanges(false);
          navigateBack();
        }}
      />
    </QueryLoader>
  );
};
